import React, {useEffect} from 'react';

import {Sequence} from 'components/Sequence';
import {AllPolymerLayouts} from 'constants/MapsConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {TEMP_SEQUENCE_ID} from 'molbio/MolBioConstants';
import {
  TranscriptionIndicesQuery,
  TranscriptionIndicesQueryVariables,
} from 'molbio/transcriptions/__generated/TranscriptionIndicesQuery';
import {TRANSCRIPTION_INDICES_QUERY} from 'molbio/transcriptions/TranscriptionConstants';
import {TranscriptionIndices, sliceBoundedTranscriptions} from 'molbio/TranscriptionsUtil';

import ComponentWrapper from './ComponentWrapper';
import {
  TRANSCRIPTION_COLOR,
  FORWARD_TRANSCRIPTION_LINE_Y_POSITION,
  REVERSE_COMPLEMENT_TRANSCRIPTION_LINE_Y_POSITION,
} from './TranscriptionViewConstants';

type TranscriptionLineLayout =
  | AllPolymerLayouts['forwardTranscriptionLine']
  | AllPolymerLayouts['reverseTranscriptionLine'];

export interface TranscriptionProps {
  xFn: (index: number) => number;
  sliceStart: number;
  sliceEnd: number;
  sequence: Sequence;
  layout: TranscriptionLineLayout;
  isReverseComplement: boolean;
  redraw: (boxHeight: number) => void;
}

interface TranscriptionLinesProps {
  transcriptionIndices: TranscriptionIndices;
  sliceStart: number;
  sliceEnd: number;
  layout: TranscriptionLineLayout;
  isReverseComplement: boolean;
  redraw: (boxHeight: number) => void;
  xFn: (index: number) => number;
  sequenceVersionIsCurrent: boolean;
}
interface TranscriptionLineProps {
  start: number;
  end: number;
  layout: TranscriptionLineLayout;
  isReverseComplement: boolean;
  xFn: (index: number) => number;
}

const BOX_HEIGHT_WITH_TRANSCRIPTIONS = 10;

function TranscriptionLine({
  start, // relative to slice start
  end, // relative to slice start
  layout,
  isReverseComplement,
  xFn,
}: TranscriptionLineProps): JSX.Element | null {
  const xTranslate = xFn(start);
  const yTranslate = isReverseComplement
    ? REVERSE_COMPLEMENT_TRANSCRIPTION_LINE_Y_POSITION
    : FORWARD_TRANSCRIPTION_LINE_Y_POSITION;
  return (
    <svg>
      <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
        <g transform={`translate(${xTranslate}, ${yTranslate})`}>
          <rect
            width={xFn(end) - xFn(start)}
            height={layout.svgHeight}
            stroke={TRANSCRIPTION_COLOR}
            fill={TRANSCRIPTION_COLOR}
            shapeRendering="geometricPrecision"
          />
        </g>
      </ComponentWrapper>
    </svg>
  );
}

function TranscriptionLines({
  transcriptionIndices,
  sliceStart,
  sliceEnd,
  layout,
  isReverseComplement,
  redraw,
  xFn,
  sequenceVersionIsCurrent,
}: TranscriptionLinesProps): JSX.Element | null {
  const sliceRelativeTranscriptions = sliceBoundedTranscriptions(
    sliceStart,
    sliceEnd,
    isReverseComplement,
    transcriptionIndices
  );

  const transcriptionsPresent = sliceRelativeTranscriptions.length > 0;
  const isVisible = layout.visible && sequenceVersionIsCurrent;

  useEffect(() => {
    if (isVisible && transcriptionsPresent) {
      redraw(BOX_HEIGHT_WITH_TRANSCRIPTIONS);
    } else {
      redraw(0);
    }
  }, [isVisible, transcriptionsPresent, redraw]);

  if (!isVisible) {
    return null;
  }

  const transcriptionLines: Array<TranscriptionLineProps> = [];
  for (const transcription of sliceRelativeTranscriptions) {
    const transcriptionStart = transcription.clampedStart;
    const transcriptionEnd = transcription.clampedEnd;
    const generateTranscriptionProps = (start: number, end: number): TranscriptionLineProps => ({
      start,
      end,
      layout,
      isReverseComplement,
      xFn,
    });
    if (transcriptionStart >= transcriptionEnd) {
      // This is a cross origin transcription that starts and ends in the slice. On the slice, it
      // requires two lines to render:
      // [sliceStart].....[transcriptionEnd](...)[transcriptionStart].....[sliceEnd]
      transcriptionLines.push(generateTranscriptionProps(transcriptionStart, sliceEnd));
      transcriptionLines.push(generateTranscriptionProps(sliceStart, transcriptionEnd));
    } else {
      transcriptionLines.push(generateTranscriptionProps(transcriptionStart, transcriptionEnd));
    }
  }
  return (
    <svg>
      {transcriptionLines.map((transcriptionLineProps, i) => (
        <TranscriptionLine {...transcriptionLineProps} key={i} />
      ))}
    </svg>
  );
}

function QueryingTranscriptionLine({
  xFn,
  sliceStart,
  sliceEnd,
  sequence,
  layout,
  isReverseComplement,
  redraw,
}: TranscriptionProps): JSX.Element | null {
  // Some temporary/preview sequences have a temporary ID that breaks graphql serialization.
  if (sequence.id === TEMP_SEQUENCE_ID) {
    return null;
  }
  return (
    <BenchlingQuery<TranscriptionIndicesQuery, TranscriptionIndicesQueryVariables>
      query={TRANSCRIPTION_INDICES_QUERY}
      variables={{sequenceId: sequence.id}}
      renderLoading={({refetch}) => null}
      fetchPolicy="cache-only" // Only reads from live updates triggered in Sequence.tsx
    >
      {(data) => {
        const transcriptionIndices: TranscriptionIndices =
          data.sequence.__typename === 'SequenceNode' ? (data.sequence.transcriptionMatches ?? []) : [];
        return (
          <TranscriptionLines
            transcriptionIndices={transcriptionIndices}
            sliceStart={sliceStart}
            sliceEnd={sliceEnd}
            layout={layout}
            isReverseComplement={isReverseComplement}
            redraw={redraw}
            xFn={xFn}
            sequenceVersionIsCurrent={sequence.get('currentVersion')}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export {QueryingTranscriptionLine as TranscriptionLine};
